import { withWebmDuration } from './webmDuration';

/**
 * Ordered by preference. VP8/Opus stays first: it is the combination the proctoring
 * backend has always received, and it is decodable everywhere WebM is supported.
 * MP4 is the Safari fallback — Safari's MediaRecorder cannot produce WebM.
 */
const MIME_TYPES = [
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp8',
    'video/webm;codecs=vp9,opus',
    'video/webm',
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4',
];

/** How often the encoder hands data over, so nothing large sits in native buffers. */
const TIMESLICE_MS = 1000;

export const pickMimeType = (): string => {
    if (typeof MediaRecorder === 'undefined') return '';
    return MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type)) ?? '';
};

/** The container actually produced, so an upload is never mislabelled. */
export const extensionForMimeType = (mimeType: string): string =>
    mimeType.includes('mp4') ? 'mp4' : 'webm';

export interface RecordedChunk {
    /** 1-based sequence number, assigned when the segment starts recording. */
    index: number;
    file: File;
    fileName: string;
    mimeType: string;
    /** Recorded length in milliseconds — also written into the file's own metadata. */
    durationMs: number;
}

export interface ChunkedRecorderOptions {
    /** Sequence number of the first segment. Defaults to 1. */
    startIndex?: number;
    videoBitsPerSecond?: number;
    audioBitsPerSecond?: number;
    /** Builds the file name for a segment. Defaults to `stream_<index>.<extension>`. */
    getFileName?: (index: number, extension: string) => string;
    /** Awaited before `flush()`/`stop()` resolve, so it can carry the upload. */
    onChunk?: (chunk: RecordedChunk) => void | Promise<void>;
    onError?: (error: Error) => void;
}

const toError = (error: unknown): Error =>
    error instanceof Error ? error : new Error(String(error));

/**
 * Records a MediaStream as a sequence of self-contained, seekable files.
 *
 * The sequence number is assigned when a segment *starts*, not when the encoder
 * finishes flushing it, so a file's name always matches the question it was recorded
 * for even though `MediaRecorder.stop()` completes asynchronously. Each closed segment
 * is handed to `onChunk` exactly once — chunks are never overwritten or dropped when
 * two of them close in quick succession.
 */
export class ChunkedRecorder {
    private readonly options: ChunkedRecorderOptions;
    private recorder: MediaRecorder | null = null;
    private stream: MediaStream | null = null;
    private closeSegment: (() => void) | null = null;
    /** Boxed so the number can still be corrected while the segment is recording. */
    private currentSegment: { index: number } | null = null;
    private nextIndex: number;
    private pending = new Set<Promise<void>>();

    constructor(options: ChunkedRecorderOptions = {}) {
        this.options = options;
        this.nextIndex = options.startIndex ?? 1;
    }

    get recording(): boolean {
        return this.recorder !== null;
    }

    /**
     * Declares the sequence number covering *now* — used when resuming an exam part-way
     * through, so the files line up with the question numbers the backend expects. A
     * segment already in flight is relabelled rather than left on the stale number.
     */
    setIndex(index: number): void {
        if (this.currentSegment) {
            this.currentSegment.index = index;
            this.nextIndex = index + 1;
            return;
        }

        this.nextIndex = index;
    }

    start(stream: MediaStream): void {
        if (this.recorder) return;
        this.stream = stream;
        this.beginSegment();
    }

    /**
     * Closes the current file and continues into the next one on the same stream.
     * The replacement recorder starts synchronously, so at most a frame is lost —
     * unlike stopping and restarting, which drops everything recorded while the
     * previous encoder flushes.
     */
    rotate(): void {
        if (!this.recorder || !this.stream) return;
        this.endSegment();
        this.beginSegment();
    }

    /** Closes the current file. Resolves once every segment has been handled. */
    async stop(): Promise<void> {
        this.endSegment();
        this.stream = null;
        await this.flush();
    }

    /** Resolves once every closed segment has been emitted and handled. */
    async flush(): Promise<void> {
        while (this.pending.size) await Promise.all([...this.pending]);
    }

    /** Drops the in-flight segment without emitting it — for unmount teardown. */
    cancel(): void {
        const recorder = this.recorder;
        this.recorder = null;
        this.closeSegment = null;
        this.currentSegment = null;
        this.stream = null;

        if (!recorder) return;
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.onerror = null;
        if (recorder.state !== 'inactive') recorder.stop();
    }

    private beginSegment(): void {
        const stream = this.stream;
        if (!stream) return;

        const segment = { index: this.nextIndex++ };
        const mimeType = pickMimeType();

        const recorderOptions: MediaRecorderOptions = {};
        if (mimeType) recorderOptions.mimeType = mimeType;
        if (this.options.videoBitsPerSecond) recorderOptions.videoBitsPerSecond = this.options.videoBitsPerSecond;
        if (this.options.audioBitsPerSecond) recorderOptions.audioBitsPerSecond = this.options.audioBitsPerSecond;

        let recorder: MediaRecorder;
        try {
            recorder = new MediaRecorder(stream, recorderOptions);
        } catch (error) {
            this.nextIndex = segment.index;
            this.options.onError?.(toError(error));
            return;
        }

        const parts: Blob[] = [];
        const startedAt = performance.now();
        let durationMs = 0;

        // `stop()` only queues the final `stop` event, so the promise that represents this
        // segment has to exist before then — otherwise `flush()` could see an empty queue
        // and report the recording as fully handled while the last file is still pending.
        let settle = () => { };
        const handled = new Promise<void>(resolve => { settle = resolve; });

        recorder.ondataavailable = ({ data }) => {
            if (data.size) parts.push(data);
        };

        recorder.onerror = () => {
            settle();
            this.options.onError?.(new Error('Recording stopped unexpectedly.'));
        };

        recorder.onstop = () => {
            void this.emit(segment.index, parts, recorder.mimeType || mimeType, durationMs).finally(settle);
        };

        try {
            recorder.start(TIMESLICE_MS);
        } catch (error) {
            this.nextIndex = segment.index;
            this.options.onError?.(toError(error));
            return;
        }

        this.recorder = recorder;
        this.currentSegment = segment;
        this.closeSegment = () => {
            // Measured when the segment is closed rather than when the encoder finishes
            // flushing, so the duration written into the file matches what was captured.
            durationMs = performance.now() - startedAt;

            if (recorder.state === 'inactive') {
                settle();
                return;
            }

            this.pending.add(handled);
            void handled.finally(() => this.pending.delete(handled));
            recorder.stop();
        };
    }

    private endSegment(): void {
        const close = this.closeSegment;
        this.recorder = null;
        this.closeSegment = null;
        this.currentSegment = null;
        close?.();
    }

    /**
     * Never rejects: a failed chunk is reported through `onError` so that `flush()` — and
     * therefore the "collecting your data" screens that await it — always settles.
     */
    private async emit(index: number, parts: Blob[], mimeType: string, durationMs: number): Promise<void> {
        if (!parts.length) return;

        try {
            const extension = extensionForMimeType(mimeType);
            const fileName = this.options.getFileName?.(index, extension) ?? `stream_${index}.${extension}`;
            const blob = await withWebmDuration(new Blob(parts, { type: mimeType }), durationMs);

            await this.options.onChunk?.({
                index,
                fileName,
                mimeType,
                durationMs,
                file: new File([blob], fileName, { type: mimeType }),
            });
        } catch (error) {
            this.options.onError?.(toError(error));
        }
    }
}
